import config from "../../../shared/config/config";
import { getWorkspaceCode } from "../../../shared/utils/auth";
import axiosAdmin from "../../../shared/utils/axiosAdmin";
import axiosAdminBlob from "../../../shared/utils/axiosAdminBlob";
import {
  ActiveUser,
  AddDeptParam,
  ChangeDeptNameParam,
  Dept,
  DeptInfo,
  DeptManagerParam,
  DeptUpload,
  ExternalUser,
  User,
  UserDTO,
  UserInsertResult,
  UserPasswd,
  UserSearchResult,
} from "../../../types/adminDept";
import { BasicReturnMsg } from "../../../types/common";
import { Member } from "../../../types/workgroup";

const GATEWAY_URL = config.servers.gateway.url;

interface AdminDeptApi {
  getDeptLists(): Promise<Dept[]>;
  getDeptDetail(deptId: string): Promise<DeptInfo[]>;
  getDeptUsers(deptId: string): Promise<User[]>;
  getDeptInfo(deptId: string): Promise<Dept>;
  getUserDetail(userId: string): Promise<User>;
  updateUserDept(userInfo: UserDTO): Promise<UserDTO[]>;
  updateExternalUserDept(userInfo: ExternalUser): Promise<Member>;
  addUserDept(userInfo: UserDTO): Promise<UserInsertResult>;
  getActiveInternalUserList(): Promise<ActiveUser[]>;
  getActiveExternalUserList(): Promise<ActiveUser[]>;
  getInActiveExternalUserList(): Promise<ActiveUser[]>;
  getExternalLiteUserList(): Promise<ActiveUser[]>;
  searchUserList(searchOpt: string): Promise<UserSearchResult>;
  getInternalUserList(searchOpt: string): Promise<UserSearchResult>;
  getExternalUserList(searchOpt: string): Promise<Member[]>;
  downloadDeptFile(): Promise<any>;
  uploadDeptFile(files: any): Promise<DeptUpload>;
  updateDeptParent(code: string, parentCode: string): Promise<Dept>;
  updateDeptManager(param: DeptManagerParam): Promise<Dept>;
  deleteDept(code: string): Promise<Dept>;
  deleteUser(list: { id: string }[]): Promise<Member[]>;
  addDept(param: AddDeptParam): Promise<Dept>;
  userPasswdUpdate(param: UserPasswd): Promise<BasicReturnMsg>;
  userPasswdInit(param: string): Promise<BasicReturnMsg>;
  changeDeptName(param: ChangeDeptNameParam): Promise<Dept>;
}

class AdminDeptServerApi implements AdminDeptApi {
  async getDeptLists(): Promise<Dept[]> {
    const resp = await axiosAdmin.get<Dept[]>(
      `${GATEWAY_URL}/wapi/admins/depts`
    );

    return Promise.resolve(resp.data);
  }

  async getDeptDetail(deptId: string): Promise<DeptInfo[]> {
    const resp = await axiosAdmin.get<DeptInfo[]>(
      `${GATEWAY_URL}/wapi/admins/depts/${deptId}/detail`
    );

    return Promise.resolve(resp.data);
  }

  async getDeptUsers(deptId: string): Promise<User[]> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<User[]>(
            `${GATEWAY_URL}/wapi/tenancy/member/admin/list`,
            {
              dsdCode: getWorkspaceCode(),
              userType: 1,
              isAdminPage: true,
              order: "user",
              tenancyCode: getWorkspaceCode(),
            }
          )
        : await axiosAdmin.get<User[]>(
            `${GATEWAY_URL}/wapi/admins/depts/${deptId}/users`
          );

    return Promise.resolve(resp.data);
  }

  async getDeptInfo(deptId: string): Promise<Dept> {
    const resp = await axiosAdmin.get<Dept>(
      `${GATEWAY_URL}/wapi/admins/depts/${deptId}`
    );

    return Promise.resolve(resp.data);
  }

  async getUserDetail(userId: string): Promise<User> {
    const resp = await axiosAdmin.get<User>(
      `${GATEWAY_URL}/wapi/admins/users/${userId}`
    );

    return Promise.resolve(resp.data);
  }

  async updateUserDept(userInfo: UserDTO): Promise<UserDTO[]> {
    const resp = await axiosAdmin.post<UserDTO[]>(
      `${GATEWAY_URL}/wapi/admins/users/update`,
      userInfo
    );

    return Promise.resolve(resp.data);
  }

  async updateExternalUserDept(userInfo: ExternalUser): Promise<Member> {
    const resp = await axiosAdmin.post<Member>(
      `${GATEWAY_URL}/wapi/admins/users/update/external`,
      { users: [userInfo] }
    );

    return Promise.resolve(resp.data);
  }

  async addUserDept(userInfo: UserDTO): Promise<UserInsertResult> {
    const resp = await axiosAdmin.post<UserInsertResult>(
      `${GATEWAY_URL}/wapi/admins/users/insert`,
      userInfo
    );

    return Promise.resolve(resp.data);
  }

  async getActiveInternalUserList(): Promise<ActiveUser[]> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/tenancy/member/admin/list`,
            {
              dsdCode: getWorkspaceCode(),
              userType: 1,
              isAdminPage: true,
              order: "user",
              tenancyCode: getWorkspaceCode(),
            }
          )
        : await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/admins/users/list/internal/active`,
            {}
          );

    return Promise.resolve(resp.data);
  }

  async getActiveExternalUserList(): Promise<ActiveUser[]> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/tenancy/member/admin/list`,
            {
              dsdCode: getWorkspaceCode(),
              userType: 2,
              isAdminPage: true,
              order: "user",
              status: 1,
              tenancyCode: getWorkspaceCode(),
            }
          )
        : await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/admins/users/list/external/active`,
            { userType: 2 }
          );

    return Promise.resolve(resp.data);
  }
  async getInActiveExternalUserList(): Promise<ActiveUser[]> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/tenancy/member/admin/list`,
            {
              dsdCode: getWorkspaceCode(),
              userType: 2,
              isAdminPage: true,
              order: "user",
              status: 2,
              tenancyCode: getWorkspaceCode(),
            }
          )
        : await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/admins/users/list/external/inactive`,
            { searchOpt: "" }
          );

    return Promise.resolve(resp.data);
  }

  async getExternalLiteUserList(): Promise<ActiveUser[]> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/tenancy/member/admin/list`,
            {
              dsdCode: getWorkspaceCode(),
              userType: 3,
              isAdminPage: true,
              order: "user",
              searchOpt: "",
              tenancyCode: getWorkspaceCode(),
            }
          )
        : await axiosAdmin.post<ActiveUser[]>(
            `${GATEWAY_URL}/wapi/admins/users/list/external/lite`,
            { searchOpt: "", userType: 3 }
          );

    return Promise.resolve(resp.data);
  }

  async getInternalUserList(searchOpt: string): Promise<UserSearchResult> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<UserSearchResult>(
            `${GATEWAY_URL}/wapi/tenancy/member/admin/list`,
            {
              dsdCode: getWorkspaceCode(),
              userType: 1,
              status: 1,
              searchOpt: searchOpt,
              isAdminPage: true,
              order: "user",
              tenancyCode: getWorkspaceCode(),
            }
          )
        : await axiosAdmin.post<UserSearchResult>(
            `${GATEWAY_URL}/wapi/admins/users/list`,
            { searchOpt: searchOpt }
          );

    return Promise.resolve(resp.data);
  }
  async searchUserList(searchOpt: string): Promise<UserSearchResult> {
    const resp = await axiosAdmin.post<UserSearchResult>(
      `${GATEWAY_URL}/wapi/admins/users/list/all`,
      { searchOpt: searchOpt }
    );

    return Promise.resolve(resp.data);
  }

  async getExternalUserList(searchOpt: string): Promise<Member[]> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<Member[]>(
            `${GATEWAY_URL}/wapi/tenancy/member/admin/list`,
            {
              dsdCode: getWorkspaceCode(),
              userTypes: [2, 3],
              isAdminPage: true,
              order: "user",
              searchOpt: searchOpt,
              tenancyCode: getWorkspaceCode(),
            }
          )
        : await axiosAdmin.post<Member[]>(
            `${GATEWAY_URL}/wapi/admins/users/list/external`,
            { searchOpt: searchOpt, userTypes: [2, 3] }
          );

    return Promise.resolve(resp.data);
  }

  async downloadDeptFile(): Promise<any> {
    try {
      const resp = await axiosAdminBlob.get<any>(
        `${GATEWAY_URL}/wapi/admins/users/list/excel`
      );

      return Promise.resolve({ data: resp.data, headers: resp.headers });
    } catch (error) {
      return Promise.resolve({ error: error });
    }
  }

  async uploadDeptFile(files: any): Promise<DeptUpload> {
    let fileData = new FormData();
    fileData.append("file", files);

    const resp = await axiosAdmin.post<DeptUpload>(
      `${GATEWAY_URL}/wapi/admins/users/list/excel/upload`,
      fileData
    );

    return Promise.resolve(resp.data);
  }

  async updateDeptParent(code: string, parentCode: string): Promise<Dept> {
    const resp = await axiosAdmin.post<Dept>(
      `${GATEWAY_URL}/wapi/admins/depts/update`,
      { code: code, parentCode: parentCode }
    );

    return Promise.resolve(resp.data);
  }

  async updateDeptManager(param: DeptManagerParam): Promise<Dept> {
    const resp = await axiosAdmin.post<Dept>(
      `${GATEWAY_URL}/wapi/admins/depts/update`,
      param
    );

    return Promise.resolve(resp.data);
  }

  async deleteDept(code: string): Promise<Dept> {
    const resp = await axiosAdmin.get<Dept>(
      `${GATEWAY_URL}/wapi/admins/depts/delete/${code}`
    );

    return Promise.resolve(resp.data);
  }

  async deleteUser(list: { id: string }[]): Promise<Member[]> {
    const resp =
      window.eco.serviceType === "OCI"
        ? await axiosAdmin.post<Member[]>(
            `${GATEWAY_URL}/wapi/tenancy/member/delete`,
            {
              dsdCode: getWorkspaceCode(),
              userIds: list.map((item) => item.id),
            }
          )
        : await axiosAdmin.post<Member[]>(
            `${GATEWAY_URL}/wapi/admins/users/delete`,
            list
          );

    return Promise.resolve(resp.data);
  }

  async addDept(param: AddDeptParam): Promise<Dept> {
    const resp = await axiosAdmin.post<Dept>(
      `${GATEWAY_URL}/wapi/admins/depts/insert`,
      param
    );

    return Promise.resolve(resp.data);
  }

  async userPasswdUpdate(param: UserPasswd): Promise<BasicReturnMsg> {
    const resp = await axiosAdmin.post<BasicReturnMsg>(
      `${GATEWAY_URL}/wapi/admins/users/passwd/update`,
      param
    );

    return Promise.resolve(resp.data);
  }

  async userPasswdInit(param: string): Promise<BasicReturnMsg> {
    const resp = await axiosAdmin.get<BasicReturnMsg>(
      `${GATEWAY_URL}/papi/passwd/init/admin/` + param
    );

    return Promise.resolve(resp.data);
  }

  async changeDeptName(param: ChangeDeptNameParam): Promise<Dept> {
    const resp = await axiosAdmin.post<Dept>(
      `${GATEWAY_URL}/wapi/admins/depts/update`,
      param
    );

    return Promise.resolve(resp.data);
  }
}
const adminDeptApi: AdminDeptApi = new AdminDeptServerApi();
export default adminDeptApi;
